import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flame, X } from "lucide-react";
import api from "@/lib/api";

/**
 * FoundingCountdownWidget — floating "N seats left" ticker beside the wishlist
 * widget. Live-polls /launch/founding-count every 60s so scarcity pressure
 * follows the user through the site.
 *
 * Hide conditions:
 *  - Countdown is full (nobody can claim → no urgency to show)
 *  - Session-dismissed (X button)
 *  - On /pricing (the counter is already visible in the page hero)
 *  - Remaining is null / not loaded yet
 */

const DISMISS_KEY = "ff2d_founding_countdown_dismissed_v1";
const POLL_MS = 60_000;

export default function FoundingCountdownWidget() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => api.get("/launch/founding-count")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    fetch();
    const id = setInterval(fetch, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  if (dismissed) return null;
  if (!data) return null;
  if (data.is_full) return null;
  if (pathname.startsWith("/pricing")) return null; // already shown in page

  const remaining = data.remaining ?? 0;
  const urgent = remaining <= 50;

  return (
    <div
      className="fixed bottom-20 left-6 z-[65] animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="founding-countdown-widget"
      role="status"
    >
      <div className={`flex items-center gap-2 rounded-full pl-2 pr-1 py-1 shadow-lg border transition-colors ${
        urgent
          ? "bg-[var(--ff-citrus)] text-white border-[var(--ff-citrus)]"
          : "bg-white text-[var(--ff-primary)] border-[var(--ff-border)]"
      }`}>
        <Link
          to="/pricing"
          className="flex items-center gap-2 pl-2 pr-1 py-1.5 group"
          data-testid="founding-countdown-cta"
        >
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${
            urgent
              ? "bg-white/20 text-white"
              : "bg-[var(--ff-citrus)]/15 text-[var(--ff-citrus)]"
          }`}>
            <Flame size={14} className={urgent ? "animate-pulse" : ""}/>
          </span>
          <span className="text-sm">
            <span className="font-semibold tabular-nums" data-testid="founding-countdown-remaining">{remaining}</span>{" "}
            <span className={urgent ? "text-white/90" : "text-[var(--ff-text-secondary)]"}>
              {urgent ? "seats left · claim now" : "founding seats left"}
            </span>
          </span>
          <span className={`text-xs font-semibold pl-1 pr-2 opacity-80 group-hover:opacity-100 transition-opacity ${
            urgent ? "text-white" : "text-[var(--ff-citrus)]"
          }`}>
            Claim →
          </span>
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className={`ml-1 mr-1 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
            urgent
              ? "text-white/70 hover:text-white hover:bg-white/15"
              : "text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] hover:bg-[var(--ff-muted)]"
          }`}
          aria-label="Dismiss founding countdown"
          data-testid="founding-countdown-dismiss"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
